import React, { useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Message } from '../types/message';
import { config } from '../config';

interface MessageListProps {
  messages: Message[];
  isBotTyping: boolean;
  user: any;
  historyChecked: boolean;
  personality?: { name: string; emoji: string };
  accentColor?: string;
  isPremium?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isBotTyping, user, historyChecked, personality, accentColor, isPremium = false }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isBotTyping]);

  const formatTime = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: Date) => {
    const today = new Date();
    const messageDate = new Date(timestamp);
    
    if (messageDate.toDateString() === today.toDateString()) {
      return 'Hoy';
    }
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    }
    
    return messageDate.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  };

  const renderMessageContent = (content: string) => {
    // Regex para detectar URLs (http, https, www)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    
    const parts = content.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Es una URL, crear enlace clickeable
        let href = part;
        if (!part.startsWith('http')) {
          href = `https://${part}`;
        }
        
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: accentColor || '#8B5CF6',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const renderMessage = (message: Message, index: number) => {
    const isUser = message.sender === 'user';
    const showDate = index === 0 || 
      new Date(message.timestamp).toDateString() !== 
      new Date(messages[index - 1].timestamp).toDateString();

    return (
      <Box key={message.id}>
        {showDate && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Chip
              label={formatDate(message.timestamp)}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                fontWeight: 500,
                fontSize: '0.8rem',
              }}
            />
          </Box>
        )}
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            mb: 2,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              maxWidth: { xs: '100%', sm: '85%', md: '75%' },
              flexDirection: isUser ? 'row-reverse' : 'row',
              minWidth: 0,
            }}
          >
            {!isUser && (
              <img 
                src={`${config.PUBLIC_URL}/images/avatar_chat.png`}
                alt="Bot Avatar"
                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
              />
            )}
            
              <Paper
              elevation={2}
              sx={{
                p: 2,
                maxWidth: '100%',
                backgroundColor: isUser 
                  ? (accentColor ? accentColor : '#8B5CF6')
                  : 'rgba(255, 255, 255, 0.05)',
                color: isUser ? 'white' : 'text.primary',
                border: isUser 
                  ? 'none'
                  : (accentColor ? `1px solid ${accentColor}33` : '1px solid rgba(139, 92, 246, 0.2)'),
                borderRadius: 2,
                position: 'relative',
                wordBreak: 'break-word',
              }}
            >
              
              {message.type === 'image' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    🖼️
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Imagen
                  </Typography>
                </Box>
              )}
              
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.5,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {renderMessageContent(message.content)}
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    fontSize: '0.75rem',
                  }}
                >
                  {formatTime(message.timestamp)}
                </Typography>
                
                
                {message.status === 'error' && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'error.main',
                      fontSize: '0.75rem',
                    }}
                  >
                    Error
                  </Typography>
                )}
              </Box>
            </Paper>
            
            {isUser && (
            <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#F59E0B',
                  fontSize: '14px',
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        py: { xs: 1.5, sm: 2 },
        px: { xs: 0.5, sm: 1 },
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.05)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(139, 92, 246, 0.3)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(139, 92, 246, 0.5)',
        },
      }}
    >
      {messages.length === 0 && historyChecked && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            px: { xs: 2, md: 4 },
          }}
        >
          <Typography 
            variant="body1" 
            color="text.primary" 
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              lineHeight: 1.7,
              maxWidth: { xs: '100%', sm: '85%', md: '650px' },
              textAlign: 'center',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            {(personality as any)?.welcomeMessage || "¡Hola! Soy Maestro Samael, experto en astrología cuántica y tarot de sueños. Si sientes curiosidad por la energía que te rodea en este momento, has tenido un sueño repetitivo que quisieras comprender, o deseas claridad sobre una situación específica que estás viviendo, estoy aquí para guiarte. ¿Sobre qué te gustaría que conversemos hoy?"}
          </Typography>
          {!isPremium && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                lineHeight: 1.7,
                maxWidth: { xs: '100%', sm: '85%', md: '650px' },
                textAlign: 'center',
                mt: 2,
                fontSize: { xs: '0.85rem', md: '0.9rem' },
                fontStyle: 'italic'
              }}
            >
              En esta versión gratuita podrás enviarme hasta 10 mensajes, así que aprovecha cada uno para hacer tus preguntas directamente.
            </Typography>
          )}
        </Box>
      )}
      
      {messages.map((message, index) => renderMessage(message, index))}
      
      {isBotTyping && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 2,
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              maxWidth: { xs: '100%', sm: '85%', md: '75%' },
              minWidth: 0,
            }}
          >
            <img 
              src={`${config.PUBLIC_URL}/images/avatar_chat.png`}
              alt="Bot Avatar"
              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
            />
            
            <Paper
              elevation={2}
              sx={{
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: accentColor ? `1px solid ${accentColor}33` : '1px solid rgba(139, 92, 246, 0.2)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {personality?.name || 'Maestro Samael'} está escribiendo...
              </Typography>
            </Paper>
          </Box>
        </Box>
      )}
      
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default MessageList;
